import moment from "moment";
import { DailyDateInfo, Holiday, ViewType } from "../types";
import { isOrgWeekend } from "../../../utils/helperUtils";

interface DateRangeConfig {
  startOffset?: { value: number; unit: moment.unitOfTime.DurationConstructor };
  endOffset?: { value: number; unit: moment.unitOfTime.DurationConstructor };
  endUnit?: moment.unitOfTime.StartOf;
  minDuration?: { value: number; unit: moment.unitOfTime.DurationConstructor };
  startUnit?: moment.unitOfTime.StartOf;
}

const VIEW_TYPE_CONFIGS: Record<ViewType, DateRangeConfig> = {
  yearly: {
    startUnit: 'year',
    endUnit: 'year',
    minDuration: { value: 2, unit: 'years' }
  },
  weekly: {
    startOffset: { value: 2, unit: 'days' },
    endOffset: { value: 2, unit: 'days' },
    minDuration: { value: 2, unit: 'months' }
  },
  quarterly: {
    startUnit: 'quarter',
    endUnit: 'quarter',
    minDuration: { value: 2, unit: 'quarters' }
  },
  monthly: {
    startOffset: { value: 7, unit: 'days' },
    endUnit: 'month',
    minDuration: { value: 3, unit: 'months' }
  }
};

export const calculateEffectiveDateRange = (
  startDate: string,
  endDate: string,
  viewType: ViewType
) => {
  const config = VIEW_TYPE_CONFIGS[viewType];
  
  let start = moment(startDate);
  let end = moment(endDate);
  
  // Apply start modifications
  if (config.startOffset) {
    start = start.subtract(config.startOffset.value, config.startOffset.unit);
  }
  if (config.startUnit) {
    start = start.startOf(config.startUnit);
  } else {
    start = start.startOf('day');
  }
  
  // Apply end modifications for effective end date calculation
  if (config.endUnit) {
    end = end.endOf(config.endUnit);
  }
  if (config.endOffset) {
    end = end.add(config.endOffset.value, config.endOffset.unit);
  }
  
  // Calculate minimum duration and adjust if needed
  if (config.minDuration) {
    const duration = end.diff(start, config.minDuration.unit);
    if (duration < config.minDuration.value) {
      const minEnd = moment(start).add(config.minDuration.value, config.minDuration.unit);
      const adjustedMinEnd = config.endUnit ? minEnd.endOf(config.endUnit) : minEnd;
      end = moment.max(end, adjustedMinEnd);
    }
  }
  
  return { start, end };
};

export const createDateInfo = (
  currentDateStr: string,
  organizationHolidays: Holiday[],
  organizationWorkingDays: any,
  viewType: ViewType
): DailyDateInfo => {
  const next_holiday_details: { date: string; holiday_name: string }[] = [];
  let nextDayIsHoliday = false;
  
  // Start checking from the very next day
  let checkDate = moment(currentDateStr).add(1, 'day');
  let foundAtLeastOneGap = false;
  let skippedInitialWeekend = false;

  // We loop to find the "next cluster"
  // If the next day(s) are weekends, we skip them.
  // Then we check if the first working day is a holiday.
  // If it is, that's the start of the cluster.
  // We collect all consecutive holidays in that cluster (skipping weekends within the cluster too).
  
  while (next_holiday_details.length < 10) { // Safety limit
    const checkDateStr = checkDate.format('YYYY-MM-DD');
    const isWeekend = isOrgWeekend(checkDateStr, organizationWorkingDays);
    
    if (isWeekend) {
      if (next_holiday_details.length === 0) {
        skippedInitialWeekend = true;
      }
      checkDate = checkDate.add(1, 'day');
      continue;
    }

    const holiday = organizationHolidays.find((h: Holiday) => h.date.split('T')[0] === checkDateStr);
    
    if (holiday) {
      if (next_holiday_details.length === 0) {
        // This is the start of our cluster
        nextDayIsHoliday = true; 
      }
      next_holiday_details.push({
        date: checkDateStr,
        holiday_name: holiday.name
      });
      checkDate = checkDate.add(1, 'day');
    } else {
      // Hit a working day that is NOT a holiday. Stop collecting.
      break;
    }
  }

  const nextDateStr = moment(currentDateStr).add(1, 'day').format('YYYY-MM-DD');
  
  const baseInfo: DailyDateInfo = {
    date: currentDateStr,
    isWeekend: false, // createDateInfo is usually called for working days due to shouldIncludeDate
    isHoliday: false,
    nextDayIsWeekend: isOrgWeekend(nextDateStr, organizationWorkingDays),
    nextDayIsHoliday: nextDayIsHoliday,
    next_holiday_details: next_holiday_details.length > 0 ? next_holiday_details : undefined,
    holidayName: next_holiday_details[0]?.holiday_name || '',
    holidayGroup: null,
    quarter: '',
    year: ''
  };
  
  // Add view-specific data
  if (viewType === 'quarterly') {
    baseInfo.quarter = moment(currentDateStr).format('[Q]Q YYYY');
    baseInfo.year = moment(currentDateStr).format('YYYY');
  } else if (viewType === 'yearly') {
    baseInfo.year = moment(currentDateStr).format('YYYY');
  }
  
  return baseInfo;
};

export const shouldIncludeDate = (
  currentDateStr: string,
  organizationHolidays: Holiday[],
  organizationWorkingDays: any,
  viewType: ViewType
): boolean => {
  if (viewType === 'yearly') {
    return true; // Yearly view includes all dates
  }
  
  const isWeekend = isOrgWeekend(currentDateStr, organizationWorkingDays);
  const isHoliday = organizationHolidays.some((h: Holiday) => h.date.split('T')[0] === currentDateStr);
  
  return !isWeekend && !isHoliday;
};

export const generateVisibleDates = (
  startDate: string,
  endDate: string,
  viewType: ViewType,
  organizationHolidays: Holiday[],
  organizationWorkingDays: any
): DailyDateInfo[] => {
  if (!startDate || !endDate) {
    return [];
  }
  
  const { start, end } = calculateEffectiveDateRange(startDate, endDate, viewType);
  const allDates: DailyDateInfo[] = [];
  const currentDate = moment(start);
  
  while (currentDate.isSameOrBefore(end)) {
    const currentDateStr = currentDate.format('YYYY-MM-DD');
    
    if (shouldIncludeDate(currentDateStr, organizationHolidays, organizationWorkingDays, viewType)) {
      allDates.push(createDateInfo(currentDateStr, organizationHolidays, organizationWorkingDays, viewType));
    }
    
    currentDate.add(1, 'day');
  }
  
  return allDates;
}; 